// =============================================================================
// Terminal Type Selector - 终端类型选择器
// =============================================================================

import React, { useState, useEffect } from 'react';
import { 
  Terminal, 
  Server, 
  Bot, 
  Code, 
  Activity, 
  Clock,
  Info,
  ChevronDown,
  Check
} from 'lucide-react';

// 终端类型图标映射
const TERMINAL_TYPE_ICONS = {
  service: Server,
  ai_tool: Bot,
  development: Code,
  monitoring: Activity,
  temporary: Clock,
  general: Terminal
};

// 终端类型颜色映射
const TERMINAL_TYPE_COLORS = {
  service: 'bg-green-100 text-green-800 border-green-200',
  ai_tool: 'bg-purple-100 text-purple-800 border-purple-200',
  development: 'bg-blue-100 text-blue-800 border-blue-200',
  monitoring: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  temporary: 'bg-gray-100 text-gray-800 border-gray-200',
  general: 'bg-slate-100 text-slate-800 border-slate-200'
};

// 持久化级别描述
const PERSISTENCE_DESCRIPTIONS = {
  high: '高持久化 - 频繁保存，长期保留',
  standard: '标准持久化 - 定期保存',
  low: '低持久化 - 基本保存',
  minimal: '最小持久化 - 很少保存'
};

export default function TerminalTypeSelector({ 
  selectedType = 'general',
  onTypeSelect,
  disabled = false,
  showDescription = true,
  compact = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailableTypes();
  }, []);

  const fetchAvailableTypes = async () => {
    try {
      console.log('🔍 Fetching terminal types...');
      const response = await fetch('/api/terminals/types/info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('📡 Terminal types response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📋 Terminal types data:', data);

      if (data.success && data.data && data.data.types) {
        console.log('✅ Setting available types:', data.data.types);
        setAvailableTypes(data.data.types);
      } else {
        console.warn('⚠️ Invalid response format:', data);
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ Failed to fetch terminal types:', error);
      // 提供默认类型作为后备
      setAvailableTypes([
        {
          id: 'general',
          name: '通用终端',
          description: '适用于一般命令和操作',
          persistence: 'standard',
          autoCleanupAfter: 14400,
          tags: ['general']
        },
        {
          id: 'service',
          name: '服务终端',
          description: '运行长期服务，如Web服务器、数据库等',
          persistence: 'high',
          autoCleanupAfter: 86400,
          tags: ['service', 'long-running']
        },
        {
          id: 'ai_tool',
          name: 'AI工具',
          description: 'Claude CLI等AI助手工具',
          persistence: 'high',
          autoCleanupAfter: 43200,
          tags: ['ai', 'interactive']
        },
        {
          id: 'development',
          name: '开发终端',
          description: '代码构建、测试、Git操作等开发任务',
          persistence: 'standard',
          autoCleanupAfter: 14400,
          tags: ['development', 'build']
        },
        {
          id: 'monitoring',
          name: '监控终端',
          description: '日志监控、系统监控等',
          persistence: 'low',
          autoCleanupAfter: 7200,
          tags: ['monitoring', 'logs']
        },
        {
          id: 'temporary',
          name: '临时终端',
          description: '快速查看、一次性任务',
          persistence: 'minimal',
          autoCleanupAfter: 1800,
          tags: ['temporary', 'quick']
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (type) => {
    if (onTypeSelect) {
      onTypeSelect(type);
    }
    setIsOpen(false);
  };

  const selectedTypeInfo = availableTypes.find(t => t.id === selectedType);
  const IconComponent = TERMINAL_TYPE_ICONS[selectedType] || Terminal;
  const typeColor = TERMINAL_TYPE_COLORS[selectedType] || TERMINAL_TYPE_COLORS.general;

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
        <span>加载终端类型...</span>
      </div>
    );
  }

  if (!availableTypes || availableTypes.length === 0) {
    return (
      <div className="flex items-center space-x-2 text-sm text-red-500">
        <span>⚠️ 无法加载终端类型</span>
        <button
          onClick={fetchAvailableTypes}
          className="text-blue-500 hover:text-blue-700 underline"
        >
          重试
        </button>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center space-x-2 px-3 py-2 border rounded-md text-sm font-medium transition-colors ${typeColor} ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-80 cursor-pointer'
          }`}
        >
          <IconComponent size={16} />
          <span>{selectedTypeInfo?.name || selectedType}</span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
            <div className="py-1">
              {availableTypes.map((type) => {
                const TypeIcon = TERMINAL_TYPE_ICONS[type.id] || Terminal;
                const isSelected = type.id === selectedType;
                
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <TypeIcon size={16} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{type.name}</span>
                        {isSelected && <Check size={14} className="text-blue-600" />}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{type.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-700">终端类型</label>
        <Info size={14} className="text-gray-400" title="选择合适的终端类型以获得最佳的性能和持久化策略" />
      </div>

      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-colors ${
            disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:bg-gray-50 cursor-pointer'
          } ${typeColor}`}
        >
          <div className="flex items-center space-x-3">
            <IconComponent size={20} />
            <div>
              <div className="font-medium">{selectedTypeInfo?.name || selectedType}</div>
              {showDescription && selectedTypeInfo?.description && (
                <div className="text-sm text-gray-500">{selectedTypeInfo.description}</div>
              )}
            </div>
          </div>
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="py-2">
              {availableTypes.map((type) => {
                const TypeIcon = TERMINAL_TYPE_ICONS[type.id] || Terminal;
                const isSelected = type.id === selectedType;
                const cleanupHours = Math.round(type.autoCleanupAfter / 3600);
                
                return (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className={`w-full flex items-start space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    <TypeIcon size={20} className={isSelected ? 'text-blue-600' : 'text-gray-600'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {type.name}
                        </span>
                        {isSelected && <Check size={16} className="text-blue-600" />}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>持久化: {PERSISTENCE_DESCRIPTIONS[type.persistence]}</span>
                        <span>自动清理: {cleanupHours}小时</span>
                      </div>
                      {type.tags && type.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {type.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedTypeInfo && showDescription && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-700">
            <div className="font-medium mb-1">选中类型详情:</div>
            <div className="space-y-1">
              <div>• 持久化级别: {PERSISTENCE_DESCRIPTIONS[selectedTypeInfo.persistence]}</div>
              <div>• 自动清理时间: {Math.round(selectedTypeInfo.autoCleanupAfter / 3600)} 小时</div>
              {selectedTypeInfo.tags && (
                <div>• 标签: {selectedTypeInfo.tags.join(', ')}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
